import { useState, useEffect, useCallback } from "react";
import {
  ExchangeClient,
  InfoClient,
  toSatoshi,
  fromSatoshi,
  toSatoshiUint64,
} from "../sdk";
import {
  LEVERAGE_DEFAULT,
  POSITION_MODE_ONE_WAY,
  MARGIN_MODE_CROSS,
  ORDER_TYPE_MARKET,
} from "../components/smartlayout/constants";

export interface TradeOperationConfig {
  baseURL: string;
  mnemonic: string;
  marketId?: string;
  marketSymbol?: string;
  selectedBucketBalance?: number; // Balance from selected bucket (nominalValue)
  tradePrice?: number; // Trade price from useTradePrice hook for market orders
  maxSizeLimit?: number; // Maximum order size limit in asset quantity (e.g., BTC)
}

export interface TradeResult {
  success: boolean;
  orderId?: string;
  error?: string;
  message?: string;
}

export function useTradeOperation(config: TradeOperationConfig) {
  const [exchangeClient, setExchangeClient] = useState<ExchangeClient | null>(
    null,
  );
  const [infoClient, setInfoClient] = useState<InfoClient | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<TradeResult | null>(null);

  // Trading state
  const [orderType, setOrderType] = useState<"market" | "limit">(
    ORDER_TYPE_MARKET,
  );
  const [fundPercent, setFundPercent] = useState<number>(0);
  const [sizeInput, setSizeInput] = useState<number>(0);
  const [price, setPrice] = useState<string>("0");
  const [leverage, setLeverage] = useState<number>(LEVERAGE_DEFAULT);
  const [positionMode, setPositionMode] = useState<"ONE_WAY" | "HEDGE">(
    POSITION_MODE_ONE_WAY,
  );
  const [marginMode, setMarginMode] = useState<"CROSS" | "ISOLATED">(
    MARGIN_MODE_CROSS,
  );

  // Balance and market data
  const [availableBalance, setAvailableBalance] = useState<number>(0);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [marketId, setMarketId] = useState<string>(config.marketId || "1");

  // Update available balance when selectedBucketBalance changes
  // This allows bucket selection to override the API-loaded balance
  useEffect(() => {
    if (config.selectedBucketBalance !== undefined) {
      // Use bucket balance if provided (even if 0, as it's an explicit selection)
      setAvailableBalance(config.selectedBucketBalance);
    }
    // If undefined, the balance will be reloaded from API by the effect below
    // when clients are ready
  }, [config.selectedBucketBalance]);

  // Initialize clients
  useEffect(() => {
    const exchange = new ExchangeClient({
      baseURL: config.baseURL,
      mnemonic: config.mnemonic,
    });
    const info = new InfoClient({
      baseURL: config.baseURL,
    });

    setExchangeClient(exchange);
    setInfoClient(info);

    // Initialize exchange client
    exchange
      .initialize()
      .then(() => {
        setIsReady(true);
      })
      .catch((error) => {
        console.error("Failed to initialize exchange client:", error);
        setResult({
          success: false,
          error: `Initialization failed: ${error.message}`,
        });
      });
  }, [config.baseURL, config.mnemonic]);

  // Load balance
  const loadBalance = useCallback(async () => {
    if (!infoClient || !exchangeClient) return;
    // Don't override balance if a bucket balance is explicitly selected
    if (config.selectedBucketBalance !== undefined) {
      return;
    }
    try {
      const walletAddress = exchangeClient.getWalletAddress();
      const balance = await infoClient.getBalanceDecimal(walletAddress, "MORM");
      if (balance && balance.available !== undefined) {
        setAvailableBalance(balance.available || 0);
      }
    } catch (error) {
      console.error("Failed to load balance:", error);
    }
  }, [infoClient, exchangeClient, config.selectedBucketBalance]);

  // Load balance when clients are ready and no bucket balance is selected
  useEffect(() => {
    if (
      isReady &&
      infoClient &&
      exchangeClient &&
      config.selectedBucketBalance === undefined
    ) {
      loadBalance();
    }
  }, [
    isReady,
    infoClient,
    exchangeClient,
    config.selectedBucketBalance,
    loadBalance,
  ]);

  // Load current market price
  const loadMarketPrice = useCallback(async () => {
    if (!infoClient) return;
    try {
      const ticker = await infoClient.getTicker(marketId);
      if (ticker && ticker.lastPrice) {
        const priceValue =
          typeof ticker.lastPrice === "string"
            ? fromSatoshi(ticker.lastPrice)
            : ticker.lastPrice;
        setCurrentPrice(priceValue);
        // Only set price if it's empty or "0" (initial state)
        if (!price || price === "0") {
          setPrice(priceValue.toString());
        }
      }
    } catch (error) {
      console.error("Failed to load market price:", error);
    }
  }, [infoClient, marketId, price]);

  // Load market price when ready
  useEffect(() => {
    if (isReady && infoClient) {
      loadMarketPrice();
    }
  }, [isReady, infoClient, loadMarketPrice]);

  // Calculate order price (market or limit)
  const getOrderPrice = useCallback((): number => {
    if (orderType === "market") {
      // Use trade price from useTradePrice hook if available, otherwise fallback to currentPrice
      return config.tradePrice && config.tradePrice > 0
        ? config.tradePrice
        : currentPrice;
    }
    return price ? parseFloat(price) : currentPrice;
  }, [orderType, price, currentPrice, config.tradePrice]);

  // Calculate available trade amount (balance * leverage)
  const getAvailableTradeAmount = useCallback((): number => {
    return availableBalance * leverage;
  }, [availableBalance, leverage]);

  // Calculate order size in BTC quantity
  const calculateSize = useCallback((): number => {
    let size: number;
    // If sizeInput is set (> 0), use it directly (it's already asset quantity)
    if (sizeInput > 0) {
      size = sizeInput;
    } else {
      // Otherwise calculate from fund percentage
      const availableTradeAmount = getAvailableTradeAmount();
      const sizeUSD = (availableTradeAmount * fundPercent) / 100;
      const orderPrice = getOrderPrice();
      if (orderPrice <= 0) return 0;
      size = sizeUSD / orderPrice;
    }
    // Apply size limit if configured - cap at maxSizeLimit
    if (
      config.maxSizeLimit !== undefined &&
      config.maxSizeLimit > 0 &&
      size > config.maxSizeLimit
    ) {
      return config.maxSizeLimit;
    }
    return size;
  }, [
    sizeInput,
    fundPercent,
    getAvailableTradeAmount,
    getOrderPrice,
    config.maxSizeLimit,
  ]);

  // Calculate order size in USD value (notional)
  const calculateSizeUSD = useCallback((): number => {
    const sizeBTC = calculateSize();
    const orderPrice = getOrderPrice();
    return sizeBTC * orderPrice;
  }, [calculateSize, getOrderPrice]);

  // Calculate order value (notional value in USD) - returns in satoshi format
  const calculateValue = useCallback((): number => {
    const sizeBTC = calculateSize();
    const orderPrice = getOrderPrice();
    const valueUSD = sizeBTC * orderPrice;
    // Convert decimal USD value to satoshi format
    return toSatoshiUint64(valueUSD);
  }, [calculateSize, getOrderPrice]);

  // Calculate margin required - returns in satoshi format
  const calculateMargin = useCallback((): number => {
    const value = calculateValue();
    if (leverage <= 0) return 0;
    // Value is already in satoshi, so divide by leverage to get margin in satoshi
    return Math.floor(value / leverage);
  }, [calculateValue, leverage]);

  // Place buy order
  const placeBuyOrder = useCallback(async (): Promise<void> => {
    if (!exchangeClient || !isReady) {
      setResult({
        success: false,
        error: "Exchange client not ready",
      });
      return;
    }

    const size = calculateSize();
    if (size <= 0) {
      setResult({
        success: false,
        error: "Invalid order size",
      });
      return;
    }

    const orderPrice = getOrderPrice();

    // Validate price for both limit and market orders
    // ExchangeClient requires price > 0 even for market orders
    if (orderPrice <= 0) {
      setResult({
        success: false,
        error:
          orderType === "limit"
            ? "Invalid price for limit order"
            : "Market price not available. Please wait for price data to load.",
      });
      return;
    }

    if (orderType === "limit" && !price) {
      setResult({
        success: false,
        error: "Price is required for limit orders",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const leverageStr = toSatoshi(leverage.toString());
      const response = await exchangeClient.placeOrder({
        marketId,
        side: "buy",
        orderType,
        price: orderPrice,
        quantity: size,
        leverage: leverageStr,
      });

      setResult({
        success: true,
        orderId: response.orderId || response.id,
        message: "Buy order placed successfully",
      });

      // Reload balance after order
      await loadBalance();
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    exchangeClient,
    isReady,
    calculateSize,
    getOrderPrice,
    orderType,
    price,
    leverage,
    marketId,
    loadBalance,
  ]);

  // Place sell order
  const placeSellOrder = useCallback(async (): Promise<void> => {
    if (!exchangeClient || !isReady) {
      setResult({
        success: false,
        error: "Exchange client not ready",
      });
      return;
    }

    const size = calculateSize();
    if (size <= 0) {
      setResult({
        success: false,
        error: "Invalid order size",
      });
      return;
    }

    const orderPrice = getOrderPrice();

    // Validate price for both limit and market orders
    // ExchangeClient requires price > 0 even for market orders
    if (orderPrice <= 0) {
      setResult({
        success: false,
        error:
          orderType === "limit"
            ? "Invalid price for limit order"
            : "Market price not available. Please wait for price data to load.",
      });
      return;
    }

    if (orderType === "limit" && !price) {
      setResult({
        success: false,
        error: "Price is required for limit orders",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const leverageStr = toSatoshi(leverage.toString());
      const response = await exchangeClient.placeOrder({
        marketId,
        side: "sell",
        orderType,
        price: orderPrice,
        quantity: size,
        leverage: leverageStr,
      });

      setResult({
        success: true,
        orderId: response.orderId || response.id,
        message: "Sell order placed successfully",
      });

      // Reload balance after order
      await loadBalance();
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    exchangeClient,
    isReady,
    calculateSize,
    getOrderPrice,
    orderType,
    price,
    leverage,
    marketId,
    loadBalance,
  ]);

  // Update size from fund percentage slider - this updates sizeInput which syncs all three places
  const handleFundPercentChange = useCallback(
    (percent: number) => {
      const availableTradeAmount = getAvailableTradeAmount();
      const sizeUSD = (availableTradeAmount * percent) / 100;
      // Calculate quantity using price for size calculation
      // Prefer trade price (from useTradePrice) if available, otherwise use currentPrice (mark price)
      const priceForSize =
        config.tradePrice && config.tradePrice > 0
          ? config.tradePrice
          : currentPrice > 0
            ? currentPrice
            : 0;
      const size = priceForSize > 0 ? sizeUSD / priceForSize : 0;
      // Round to 8 decimal places for precision
      let roundedSize =
        isNaN(size) || !isFinite(size) ? 0 : Math.round(size * 1e8) / 1e8;
      // Apply size limit if configured - cap at maxSizeLimit
      if (
        config.maxSizeLimit !== undefined &&
        config.maxSizeLimit > 0 &&
        roundedSize > config.maxSizeLimit
      ) {
        roundedSize = config.maxSizeLimit;
        // Recalculate percentage based on the capped size to keep slider in sync
        const orderPrice = getOrderPrice();
        if (roundedSize > 0 && availableTradeAmount > 0 && orderPrice > 0) {
          const cappedSizeUSD = roundedSize * orderPrice;
          const actualPercent = (cappedSizeUSD / availableTradeAmount) * 100;
          setFundPercent(Math.min(100, Math.max(0, actualPercent)));
        } else {
          setFundPercent(percent);
        }
      } else {
        setFundPercent(percent);
      }
      setSizeInput(roundedSize);
    },
    [
      getAvailableTradeAmount,
      currentPrice,
      config.tradePrice,
      config.maxSizeLimit,
      getOrderPrice,
    ],
  );

  // Update fund percentage from size input
  const handleSizeInputChange = useCallback(
    (value: number) => {
      let numValue = isNaN(value) || value < 0 ? 0 : value;
      // Apply size limit if configured - cap at maxSizeLimit
      if (
        config.maxSizeLimit !== undefined &&
        config.maxSizeLimit > 0 &&
        numValue > config.maxSizeLimit
      ) {
        numValue = config.maxSizeLimit;
      }
      // Use the actual React setter directly
      setSizeInput(numValue);
      const availableTradeAmount = getAvailableTradeAmount();
      const orderPrice = getOrderPrice();
      if (numValue > 0 && availableTradeAmount > 0 && orderPrice > 0) {
        // Convert asset quantity to USD, then calculate fund percentage
        const sizeUSD = numValue * orderPrice;
        const percent = (sizeUSD / availableTradeAmount) * 100;
        setFundPercent(Math.min(100, Math.max(0, percent)));
      } else {
        setFundPercent(0);
      }
    },
    [getAvailableTradeAmount, getOrderPrice, config.maxSizeLimit],
  );

  return {
    // State
    orderType,
    setOrderType,
    fundPercent,
    sizeInput,
    price,
    leverage,
    positionMode,
    marginMode,
    availableBalance,
    currentPrice,
    marketId,
    isReady,
    loading,
    result,

    // Actions
    setFundPercent: handleFundPercentChange,
    setSizeInput: handleSizeInputChange,
    setPrice,
    setLeverage,
    setPositionMode,
    setMarginMode,
    setMarketId,
    placeBuyOrder,
    placeSellOrder,
    loadBalance,
    loadMarketPrice,

    // Calculations
    calculateSize,
    calculateSizeUSD,
    calculateValue,
    calculateMargin,
    getOrderPrice,
    getAvailableTradeAmount,
  };
}
